import React, { useEffect, useState } from "react";
import {
    Table,
    Button,
    Typography,
    Modal,
    Form,
    Input,
    Upload,
    message,
    notification,
    Image,
    Space,
    Card,
    Select,
    Tag,
} from "antd";
import { PlusOutlined, UploadOutlined, EditOutlined } from "@ant-design/icons";
import axios from "axios";
import { getIdeas, addIdea, updateIdea } from "../api/api";
import { cdnUrl } from "../utils/url";
import moment from "moment";
import Spinner from "../components/Spinner";
import { uploader } from "../utils/upload";
import { siteInfo } from "../utils/SiteInfo";
import { useSelector } from "react-redux";
import ResponsiveTable from "../components/ResponsiveTable";
import { isPermitted } from "../utils/PermissionManager";



const { Column } = Table;
const { TextArea } = Input;
const { Option } = Select;
const { Title, Text } = Typography;

const STATUS_MAP = {
    1: { label: "Pending", color: "orange" },
    2: { label: "Reviewed", color: "blue" },
    5: { label: "Shortlisted", color: "purple" },
    9: { label: "Best", color: "green" },
};

export default function IdeasForAll({ }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingIdea, setEditingIdea] = useState(null);
    const [form] = Form.useForm();
    const [fileList, setFileList] = useState([]);
    const [filter, setFilter] = useState({ page: 1 });
    const [recalculate, setRecalculate] = useState(0);
    const [viewDesc, setViewDesc] = useState(null);
    const profile = useSelector((state) => state.auth.profile);
    const roles = useSelector((state) => state.auth.profile?.roles);

    const checkIsMyIdea = (idea) => {
        if (!idea) return true;
        const ideaAdminId = idea.admin?._id || idea.admin || idea.created_by?._id || idea.created_by || idea.createdBy?._id || idea.createdBy || idea.user?._id || idea.user;
        return ideaAdminId === profile?._id;
    };


    useEffect(() => {
        setLoading(true);
        let mFilter = filter ? { ...filter } : {};
        getIdeas((res) => {
            setData(res);
            setLoading(false);
        }, mFilter);
    }, [filter, recalculate]);

    const openNotification = (type, messageText, description) => {
        notification[type]({
            message: messageText,
            description: description,
        });
    };

    const handleUploadChange = ({ fileList: newFileList }) => {
        setFileList(newFileList);
    };

    const onFinish = async (values) => {
        setLoading(true);
        try {
            const isMyIdea = !editingIdea || checkIsMyIdea(editingIdea);
            const newFilesToUpload = fileList.filter((file) => file.originFileObj);
            const uploadedImages = newFilesToUpload.length > 0
                ? await uploader(newFilesToUpload, "image", "administration")
                : [];

            const existingImages = [];
            for (const file of fileList) {
                if (!file.originFileObj) {
                    if (file._id) {
                        existingImages.push(file._id);
                    } else if (typeof file === "string") {
                        existingImages.push(file);
                    }
                }
            }

            const finalImages = [...existingImages, ...uploadedImages];

            const ideaData = {
                title: isMyIdea ? values.title : editingIdea.title,
                description: isMyIdea ? values.description : editingIdea.description,
                images: isMyIdea ? finalImages : (editingIdea.images || []).map(img => typeof img === "object" ? img._id : img),
                status: values.status || 1,
            };

            if (editingIdea) {
                updateIdea(editingIdea._id, ideaData, (success) => {
                    setLoading(false);
                    if (success) {
                        openNotification("success", "Success", "Idea updated successfully!");
                        setModalOpen(false);
                        form.resetFields();
                        setFileList([]);
                        setEditingIdea(null);
                        setRecalculate((prev) => prev + 1);
                    } else {
                        openNotification("error", "Error", "Failed to update idea.");
                    }
                });
            } else {
                addIdea(ideaData, (success) => {
                    setLoading(false);
                    if (success) {
                        openNotification("success", "Success", "Idea added successfully!");
                        setModalOpen(false);
                        form.resetFields();
                        setFileList([]);
                        setRecalculate((prev) => prev + 1);
                    } else {
                        openNotification("error", "Error", "Failed to add idea.");
                    }
                });
            }
        } catch (error) {
            console.error(error);
            setLoading(false);
            openNotification("error", "Error", "An error occurred while saving the idea.");
        }
    };

    const handleEdit = (record) => {
        setEditingIdea(record);
        form.setFieldsValue({
            title: record.title,
            description: record.description,
            status: record.status || 1,
        });

        const existingFiles = (record.images || []).map((img, idx) => {
            const src = typeof img === "object" && img.url ? img.url : (typeof img === "string" ? (img.startsWith("http") ? img : `${cdnUrl}/${img}`) : "");
            return {
                uid: `existing-${idx}`,
                name: `Image ${idx + 1}`,
                status: "done",
                url: src,
                _id: typeof img === "object" ? img._id : img,
            };
        });
        setFileList(existingFiles);
        setModalOpen(true);
    };

    return (
        <div >
            {!isPermitted(roles, ['master_admin']) && <div
                style={{
                    display: "flex",
                    marginBottom: "1rem",
                    backgroundColor: "#fff",
                    justifyContent: "space-between",
                    padding: 12,
                    alignItems: "center",
                    borderRadius: 6,
                    flexWrap: "wrap",
                    gap: "10px",
                }}
            >
                <Title level={5} style={{ margin: 0, display: "flex", alignItems: "center" }}>
                    Ideas
                </Title>
                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center" }}>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        style={{ marginLeft: "10px" }}
                        onClick={() => {
                            setEditingIdea(null);
                            setFileList([]);
                            form.resetFields();
                            setModalOpen(true);
                        }}
                    >
                        Add Idea
                    </Button>
                </div>
            </div>}

            <div style={{
                display: 'flex',
                flexWrap: "wrap",
                gap: "10px",
                marginBottom: "0.5rem",
            }}>
                {[
                    { title: "All", value: undefined },
                    { title: "Pending", value: 1 },
                    { title: "Reviewed", value: 2 },
                    { title: "Shortlisted", value: 5 },
                    { title: "Best", value: 9 },
                ].map((el) => (
                    <div
                        key={el.title}
                        onClick={() => {
                            let prev = { ...filter, page: 1 };
                            if (el.value === undefined) {
                                delete prev.status;
                            } else {
                                prev.status = el.value;
                            }
                            setFilter(prev);
                        }}
                        style={{
                            padding: "0.5rem 0.75rem",
                            cursor: "pointer",
                            backgroundColor:
                                filter?.status === el.value
                                    ? siteInfo.navColor
                                    : "#fff",
                            color:
                                filter?.status === el.value ? "#fff" : "#000",
                            borderRadius: 4,
                            fontSize: 13,
                            fontWeight: filter?.status === el.value ? "bold" : "normal",
                        }}
                    >
                        {el.title}
                    </div>
                ))}
            </div>

            {loading && !data ? (
                <Spinner />
            ) : data && data.docs ? (
                <ResponsiveTable>
                    <Table
                        dataSource={data.docs}
                        rowKey={(record) => record._id}
                        pagination={{
                            current: filter?.page || 1,
                            pageSize: data?.limit || 10,
                            total: data?.totalDocs || 0,
                            showSizeChanger: false,
                        }}
                        onChange={(page) => {
                            setFilter({ ...filter, page: page.current });
                        }}
                    >
                        <Column
                            title="Date"
                            dataIndex="createdAt"
                            key="createdAt"
                            render={(text) => (
                                <Text>
                                    {moment(text).format("DD MMM YYYY, hh:mm A")}
                                </Text>
                            )}
                        />
                        <Column
                            title="Submitted By"
                            dataIndex="admin"
                            key="admin"
                            render={(admin) => <Text>{admin?.name || "N/A"}</Text>}
                        />
                        <Column
                            title="Title"
                            dataIndex="title"
                            key="title"
                            render={(text) => <Text style={{ fontWeight: "bold" }}>{text}</Text>}
                        />
                        <Column
                            title="Description"
                            dataIndex="description"
                            key="description"
                            width={200}
                            render={(text) => {
                                if (!text) return <Text type="secondary">-</Text>;
                                if (text.length > 60) {
                                    return (
                                        <div
                                            onClick={() => setViewDesc(text)}
                                            style={{ cursor: "pointer" }}
                                        >
                                            {text.slice(0, 60)}...{" "}
                                            <span style={{ color: siteInfo.navColor || "#1890ff", fontWeight: "bold" }}>
                                                More
                                            </span>
                                        </div>
                                    );
                                }
                                return <span>{text}</span>;
                            }}
                        />
                        <Column
                            title="Images"
                            dataIndex="images"
                            key="images"
                            align="center"
                            render={(images) => (
                                <Space size="small">
                                    {images && images.map((img, idx) => {
                                        const src = typeof img === "object" && img.url ? img.url : (typeof img === "string" ? (img.startsWith("http") ? img : `${cdnUrl}/${img}`) : "");
                                        return (
                                            <Image
                                                key={idx}
                                                src={src}
                                                alt="Idea attachment"
                                                style={{
                                                    width: 50,
                                                    height: 50,
                                                    objectFit: "cover",
                                                    borderRadius: 4,
                                                    border: "1px solid #ddd",
                                                }}
                                                fallback={require("../assets/imageFallback.svg").default}
                                            />
                                        );
                                    })}
                                </Space>
                            )}
                        />
                        <Column
                            title="Status"
                            dataIndex="status"
                            key="status"
                            align="center"
                            render={(status) => {
                                const info = STATUS_MAP[status] || { label: "Pending", color: "orange" };
                                return <Tag color={info.color}>{info.label}</Tag>;
                            }}
                        />
                        <Column
                            title="Action"
                            key="action"
                            align="center"
                            render={(_, record) => {
                                let allowEdit = true
                                if (record?.status !== 1) {
                                    allowEdit = false
                                }
                                if (isPermitted(roles, ['master_admin'])) {
                                    allowEdit = true
                                }

                                return (
                                    <Button
                                        type="primary"
                                        disabled={!allowEdit}
                                        onClick={() => handleEdit(record)}
                                    >
                                        Edit
                                    </Button>
                                );
                            }}
                        />
                    </Table>
                </ResponsiveTable>
            ) : (
                <Card>
                    <Text>No ideas found.</Text>
                </Card>
            )}

            <Modal
                title={editingIdea ? "Edit Idea" : "Add New Idea"}
                open={modalOpen}
                onCancel={() => {
                    setModalOpen(false);
                    form.resetFields();
                    setFileList([]);
                    setEditingIdea(null);
                }}
                footer={null}
                width={600}
            >
                {editingIdea && !checkIsMyIdea(editingIdea) && (
                    <div style={{ marginBottom: 16, padding: "8px 12px", backgroundColor: "#f5f5f5", borderRadius: 4 }}>
                        <Text type="secondary">Submitted by: </Text>
                        <Text strong>{editingIdea.admin?.name || "N/A"}</Text>
                    </div>
                )}
                <Form
                    style={{
                        marginTop: 16
                    }}
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    initialValues={{ title: "", description: "", status: 1 }}
                >
                    <Form.Item
                        name="title"
                        label="Title"
                        rules={[{ required: !editingIdea || checkIsMyIdea(editingIdea), message: "Please input the title of the idea!" }]}
                    >
                        <Input placeholder="Enter idea title" disabled={editingIdea && !checkIsMyIdea(editingIdea)} />
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label="Description"
                        rules={[{ required: !editingIdea || checkIsMyIdea(editingIdea), message: "Please input the description!" }]}
                    >
                        <TextArea rows={6} placeholder="Enter idea description" disabled={editingIdea && !checkIsMyIdea(editingIdea)} />
                    </Form.Item>

                    {editingIdea && isPermitted(roles, ['master_admin']) && (
                        <Form.Item
                            name="status"
                            label="Status"
                            rules={[{ required: true, message: "Please select status!" }]}
                        >
                            <Select placeholder="Select status">
                                <Option value={1}>Pending</Option>
                                <Option value={2}>Reviewed</Option>
                                <Option value={5}>Shortlisted</Option>
                                <Option value={9}>Best</Option>
                            </Select>
                        </Form.Item>
                    )}

                    <Form.Item label="Images">
                        <Upload
                            listType="picture"
                            fileList={fileList}
                            onChange={handleUploadChange}
                            beforeUpload={() => false}
                            multiple
                            accept="image/*"
                            disabled={editingIdea && !checkIsMyIdea(editingIdea)}
                            showUploadList={{
                                showRemoveIcon: !editingIdea || checkIsMyIdea(editingIdea)
                            }}
                        >
                            {(!editingIdea || checkIsMyIdea(editingIdea)) && (
                                <Button icon={<UploadOutlined />}>Select Images</Button>
                            )}
                        </Upload>
                    </Form.Item>

                    <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
                        <Button onClick={() => {
                            setModalOpen(false);
                            setEditingIdea(null);
                        }}>Cancel</Button>
                        <Button type="primary" htmlType="submit" loading={loading}>
                            Submit
                        </Button>
                    </div>
                </Form>
            </Modal>

            <Modal
                title="Description"
                open={!!viewDesc}
                onCancel={() => setViewDesc(null)}
                footer={[
                    <Button key="close" onClick={() => setViewDesc(null)}>
                        Close
                    </Button>
                ]}
            >
                <div style={{ whiteSpace: "pre-wrap", maxHeight: "60vh", overflowY: "auto" }}>
                    {viewDesc}
                </div>
            </Modal>
        </div>
    );
}
